import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useSafeInView } from "@/components/Motion";
import { Img } from "@/components/Media";

const EASE = [0.22, 1, 0.36, 1];

/**
 * Work card with a real project thumbnail.
 *
 * Until `heroImage` is supplied, <Img> renders a placeholder generated from the study's
 * slug and accent — so each card is visually distinct rather than the same giant letter
 * on the same gradient. Thumbnails now show on mobile too.
 */
const CaseCard = ({ study, index = 0, headingLevel: Heading = "h3" }) => {
  const [ref, visible] = useSafeInView("-40px");
  return (
    <motion.div
      ref={ref}
      initial={{ clipPath: "inset(100% 0 0 0)" }}
      animate={visible ? { clipPath: "inset(0% 0 0 0)" } : {}}
      transition={{ duration: 1, delay: index * 0.08, ease: EASE }}
      data-testid={`case-card-${study.slug}`}
    >
      <Link to={`/work/${study.slug}`} className="group block" data-testid={`case-card-link-${study.slug}`}>
        <div className="relative overflow-hidden rounded-3xl border border-white/10 transition-colors duration-500 group-hover:border-white/25">
          <Img
            src={study.heroImage}
            alt={study.heroLabel || `${study.title} — project preview`}
            label={study.heroLabel || study.client}
            ratio="4/3"
            accent={study.accent}
            seed={`card-${study.slug}`}
            className="transition-transform duration-700 group-hover:scale-[1.04]"
            priority={index === 0}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-ink/40" aria-hidden="true" />
          <div className="pointer-events-none absolute left-5 top-5 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/70 sm:left-6 sm:top-6 sm:text-[11px]">
            <span className="rounded-full border border-white/20 bg-ink/50 px-3 py-1 backdrop-blur-sm" style={{ color: study.accent }}>
              {study.category}
            </span>
            <span className="rounded-full border border-white/15 bg-ink/50 px-3 py-1 backdrop-blur-sm">{study.year}</span>
          </div>
          <ArrowUpRight
            className="pointer-events-none absolute bottom-5 right-5 h-6 w-6 transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 sm:bottom-6 sm:right-6 sm:h-7 sm:w-7"
            style={{ color: study.accent }}
            aria-hidden="true"
          />
        </div>
        <Heading className="mt-5 text-lg font-bold tracking-tight text-white transition-colors duration-300 group-hover:text-white/80 sm:text-xl md:text-2xl">
          {study.title}
        </Heading>
        <p className="mt-2 line-clamp-2 max-w-lg text-sm leading-relaxed text-white/50">{study.summary}</p>
      </Link>
    </motion.div>
  );
};

export default CaseCard;
